import type { fetchFeaturesFamilyMembersOrThrow } from "../../api/fetch.ts";
import type { ContigSwapUpdate, MemberItem } from "../../store/slices/main-slice.ts";
import type { MiniFamily } from "../../synteny-worker/state.ts";
import type { Track, TrackGroup, TrackGroups } from "../../types.ts";

/**
 * Calculates which track groups need their contig swapped
 * RULE: Never swap contigs for tracks that are in the aligned family (same family scenario)
 * For new families: Prioritize contig swaps to maximize family member visibility
 */
export function calculateContigSwaps(
  to: MemberItem,
  allTrackGroups: TrackGroups,
  familyMembers: Awaited<ReturnType<typeof fetchFeaturesFamilyMembersOrThrow>>,
  alignedFamily?: MiniFamily,
  tracks?: Map<string, Track>,
): ContigSwapUpdate[] {
  const { trackGroupId: triggerTrackGroupId, contigId: selectedMemberContigId } = to;

  const triggerTrackGroup = allTrackGroups.get(triggerTrackGroupId);
  if (!triggerTrackGroup) {
    return [];
  }

  const initialTriggerContigId = triggerTrackGroup.contigId;
  const isSameFamily = alignedFamily?.familyId === to.familyId;

  const alignedTrackGroupIds = new Set<string>();
  if (isSameFamily && alignedFamily?.members && tracks) {
    for (const member of alignedFamily.members) {
      const track = tracks.get(member.trackId);
      if (track) {
        alignedTrackGroupIds.add(track.groupId);
      }
    }
  }

  const updates: ContigSwapUpdate[] = [];

  const triggerNeedsSwap = selectedMemberContigId !== initialTriggerContigId;
  const triggerIsAligned = alignedTrackGroupIds.has(triggerTrackGroupId);

  if (triggerNeedsSwap && !triggerIsAligned) {
    updates.push({
      groupId: triggerTrackGroupId,
      newContigId: selectedMemberContigId,
      toBase: to.start,
    });
  }

  const organismData = buildOrganismData(familyMembers);

  for (const [organismId, { contigsWithMembers, leftMostMap }] of organismData) {
    const tracksForOrganism = Array.from(allTrackGroups.values()).filter((group) => group.organismId === organismId);

    if (tracksForOrganism.length === 0) continue;

    const swapUpdates = calculateOrganismSwaps(
      tracksForOrganism,
      contigsWithMembers,
      leftMostMap,
      triggerTrackGroupId,
      alignedTrackGroupIds,
      isSameFamily,
    );

    updates.push(...swapUpdates);
  }

  return updates;
}

function buildOrganismData(familyMembers: Awaited<ReturnType<typeof fetchFeaturesFamilyMembersOrThrow>>): Map<
  number,
  {
    contigsWithMembers: number[];
    leftMostMap: Map<number, number>;
    allTrackGroupsWithMembers: Set<string>;
  }
> {
  const organismDataMap = new Map();

  for (const organism of familyMembers) {
    const contigsWithMembers: number[] = [];
    const leftMostMap = new Map<number, number>();
    const allTrackGroupsWithMembers = new Set<string>();

    for (const contig of organism.contigs) {
      if (contig.members.length > 0) {
        contigsWithMembers.push(contig.contigId);
        const leftMost = Math.min(...contig.members.map((m) => m.start));
        leftMostMap.set(contig.contigId, leftMost);
      }
    }

    if (contigsWithMembers.length > 0) {
      organismDataMap.set(organism.organismId, {
        contigsWithMembers,
        leftMostMap,
        allTrackGroupsWithMembers,
      });
    }
  }

  return organismDataMap;
}

function calculateOrganismSwaps(
  tracksForOrganism: TrackGroup[],
  contigsWithMembers: number[],
  leftMostMap: Map<number, number>,
  triggerTrackGroupId: string,
  alignedTrackGroupIds: Set<string>,
  isSameFamily: boolean,
): ContigSwapUpdate[] {
  if (isSameFamily) {
    return calculateConservativeSwaps(
      tracksForOrganism,
      contigsWithMembers,
      leftMostMap,
      triggerTrackGroupId,
      alignedTrackGroupIds,
    );
  }

  return calculateAggressiveSwaps(tracksForOrganism, contigsWithMembers, leftMostMap, triggerTrackGroupId);
}

function calculateConservativeSwaps(
  tracksForOrganism: TrackGroup[],
  contigsWithMembers: number[],
  leftMostMap: Map<number, number>,
  triggerTrackGroupId: string,
  alignedTrackGroupIds: Set<string>,
): ContigSwapUpdate[] {
  const updates: ContigSwapUpdate[] = [];
  const shownContigs = new Set<number>();

  for (const track of tracksForOrganism) {
    if (alignedTrackGroupIds.has(track.groupId)) {
      shownContigs.add(track.contigId);
    }
  }

  for (const track of tracksForOrganism) {
    if (track.groupId === triggerTrackGroupId || alignedTrackGroupIds.has(track.groupId)) {
      continue;
    }

    if (contigsWithMembers.includes(track.contigId)) {
      shownContigs.add(track.contigId);
    }
  }

  const tracksNeedingSwap = tracksForOrganism.filter((track) => {
    if (track.groupId === triggerTrackGroupId || alignedTrackGroupIds.has(track.groupId)) {
      return false;
    }

    return !contigsWithMembers.includes(track.contigId);
  });

  const availableContigs = contigsWithMembers.filter((contigId) => !shownContigs.has(contigId));
  const numAssignments = Math.min(tracksNeedingSwap.length, availableContigs.length);

  for (let i = 0; i < numAssignments; i++) {
    const contigId = availableContigs[i];
    const leftMost = leftMostMap.get(contigId);

    updates.push({
      groupId: tracksNeedingSwap[i].groupId,
      newContigId: contigId,
      toBase: leftMost,
    });
  }

  return updates;
}

/**
 * Aggressive swapping for new families: maximize family member visibility
 * Redistribute ALL tracks across available contigs for optimal coverage
 * Only used for NEW families (isSameFamily = false)
 */
function calculateAggressiveSwaps(
  tracksForOrganism: TrackGroup[],
  contigsWithMembers: number[],
  leftMostMap: Map<number, number>,
  triggerTrackGroupId: string,
): ContigSwapUpdate[] {
  const updates: ContigSwapUpdate[] = [];
  const redistributableTrackGroups = tracksForOrganism.filter((track) => track.groupId !== triggerTrackGroupId);

  const sortedContigsWithMembers = [...contigsWithMembers].sort((a, b) => {
    const aLeftMost = leftMostMap.get(a) ?? 0;
    const bLeftMost = leftMostMap.get(b) ?? 0;
    return aLeftMost - bLeftMost;
  });

  const triggerTrack = tracksForOrganism.find((t) => t.groupId === triggerTrackGroupId);
  const contigAssignments = new Map<number, string[]>();

  if (triggerTrack && contigsWithMembers.includes(triggerTrack.contigId)) {
    contigAssignments.set(triggerTrack.contigId, [triggerTrack.groupId]);
  }

  if (redistributableTrackGroups.length === 0) {
    return updates;
  }

  const maxTracksPerContig = Math.max(
    1,
    Math.ceil(redistributableTrackGroups.length / sortedContigsWithMembers.length),
  );

  let contigIndex = 0;

  for (const track of redistributableTrackGroups) {
    let assignedContig: number | undefined;

    for (const contigId of sortedContigsWithMembers) {
      const currentAssignments = contigAssignments.get(contigId) || [];

      if (currentAssignments.length < maxTracksPerContig) {
        assignedContig = contigId;
        contigAssignments.set(contigId, [...currentAssignments, track.groupId]);
        break;
      }
    }

    if (assignedContig == undefined) {
      const contigId = sortedContigsWithMembers[contigIndex % sortedContigsWithMembers.length];
      const currentAssignments = contigAssignments.get(contigId) || [];

      assignedContig = contigId;
      contigAssignments.set(contigId, [...currentAssignments, track.groupId]);
      contigIndex++;
    }

    if (assignedContig != undefined && assignedContig !== track.contigId) {
      const leftMost = leftMostMap.get(assignedContig);
      updates.push({
        groupId: track.groupId,
        newContigId: assignedContig,
        toBase: leftMost,
      });
    }
  }

  return updates;
}
